import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import colors from '../styles/colors';
import { useTranslation } from 'react-i18next';
import { getMenuData } from '../data/menuData';
import { useMultiLanguage } from '../hooks/useMultiLanguage';
import logo from '../assets/images/logo.png';

const PrintStyles = createGlobalStyle`
  @page { size: A4; margin: 8mm; }
  @media print {
    body { 
      -webkit-print-color-adjust: exact; 
      print-color-adjust: exact; 
      font-size: 11px;
    }
    .no-print { display: none !important; }
    .page-break { page-break-after: always; }
    section { border: none !important; }
  }
`;

const Wrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  color: ${colors.text.primary};
  font-family: Arial, Helvetica, sans-serif;
  font-size: 11px;
`;

const HeaderBar = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 8px 0;
  padding: 5px 0;
  border-bottom: 2px solid ${colors.primary.green};
`;

const Title = styled.h1`
  font-size: 20px;
  margin: 0;
  color: ${colors.primary.green};
`;

const Logo = styled.img`
  height: 36px;
`;

const PrintBtn = styled.button`
  padding: 5px 10px;
  border: 2px solid ${colors.primary.green};
  background: #fff;
  color: ${colors.primary.green};
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 12px;
  &:hover {
    background: ${colors.primary.green};
    color: #fff;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  
  @media print {
    grid-template-columns: 1fr 1fr;
  }
`;

const Section = styled.section`
  background: #fff;
  border: 1px solid ${colors.primary.green}30;
  border-radius: 6px;
  padding: 6px 10px;
  margin-bottom: 7px;
  break-inside: avoid;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 5px;
  h2 { 
    color: ${colors.primary.green}; 
    margin: 0; 
    font-size: 13px;
    font-weight: 700;
  }
  p { 
    color: ${colors.text.secondary}; 
    margin: 0; 
    font-size: 10px; 
  }
`;

const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 5px;
  padding: 3.5px 0;
  border-bottom: 1px dashed #e3e3e3;
  &:last-child {
    border-bottom: none;
  }
`;

const ItemName = styled.div`
  font-weight: 700;
  font-size: 11px;
  line-height: 1.3;
`;

const ItemDesc = styled.div`
  color: ${colors.text.secondary};
  font-size: 9.5px;
  margin-top: 1px;
  line-height: 1.3;
`;

const ItemDescSecondary = styled.div`
  color: ${colors.text.secondary}B3;
  font-size: 8.5px;
  font-style: italic;
  line-height: 1.2;
`;

const ItemPrice = styled.div`
  font-weight: 800;
  white-space: nowrap;
  color: ${colors.primary.green};
  font-size: 11px;
`;

const Note = styled.p`
  margin: 5px 0 0 0;
  color: ${colors.primary.blue};
  font-style: italic;
  font-size: 9px;
`;

const RenderBlock = ({ t, getTranslations, keyName }) => {
  const block = getMenuData(keyName);
  if (!block) return null;
  return (
    <Section>
      <SectionHeader>
        <h2>{t(`menu.${keyName}`, { defaultValue: block.title })}</h2>
        <p>{t(`menu.${keyName}Subtitle`, { defaultValue: block.subtitle || '' })}</p>
      </SectionHeader>
      {block.items.map((it) => {
        const name = getTranslations(it.id, 'name');
        const desc = getTranslations(it.id, 'description');
        return (
          <ItemRow key={`${keyName}-${it.id}`}>
            <div>
              <ItemName>{name.mainText || it.name}</ItemName>
              {(desc.mainText || it.description) && (
                <ItemDesc>{desc.mainText || it.description}</ItemDesc>
              )}
              {desc.secondaryTexts.map((s, idx) => (
                <ItemDescSecondary key={idx}>{s.text}</ItemDescSecondary>
              ))}
              {it.unit && (
                <Note>
                  {t('menu.pricePer')} {it.unit}
                </Note>
              )}
              {it.note && (<Note>{it.note}</Note>)}
            </div>
            <ItemPrice>{it.price}</ItemPrice>
          </ItemRow>
        );
      })}
      {block.note && (<Note>{t('menu.pratosNote')}</Note>)}
    </Section>
  );
};

const PrintableLunchMenu = () => {
  const { t, i18n } = useTranslation();
  const { getTranslations } = useMultiLanguage();
  const currentLang = i18n.language;

  return (
    <Wrapper>
      <PrintStyles />
      <HeaderBar>
        <Logo src={logo} alt="Cuore Brasiliano" />
        <Title>{currentLang === 'it' ? 'Menu Pranzo' : 'Cardápio de Almoço'}</Title>
        <PrintBtn className="no-print" onClick={() => window.print()}>{t('menu.print', { defaultValue: 'Imprimir' })}</PrintBtn>
      </HeaderBar>

      <ContentGrid>
        <div>
          <RenderBlock t={t} getTranslations={getTranslations} keyName="pratos" />
        </div>
        <div>
          <RenderBlock t={t} getTranslations={getTranslations} keyName="sobremesas" />
        </div>
      </ContentGrid>
    </Wrapper>
  );
};

export default PrintableLunchMenu;


